import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Key, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";

const EMPTY = { name: "", mobile_number: "", employee_id: "", password: "", department: "", designation: "" };

const Field = ({ label, testId, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      data-testid={testId}
      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
      {...props}
    />
  </div>
);

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/proxy/employees").then((r) => setEmployees(r.data)).catch(() => toast.error("Could not load employees — try again shortly")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addEmployee = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/proxy/employees", form);
      toast.success(`${form.name} added to the team`);
      setAddOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed to add employee");
    } finally { setBusy(false); }
  };

  const updateEmployee = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { name, department, designation, status } = form;
      await api.patch(`/proxy/employees/${editing.id}`, { name, department, designation, status });
      toast.success("Employee updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Update failed");
    } finally { setBusy(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/proxy/employees/${resetTarget.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Reset failed");
    } finally { setBusy(false); }
  };

  const deleteEmployee = async () => {
    try {
      await api.delete(`/proxy/employees/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} removed`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Delete failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="employees-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Team Management</p>
          <h1 className="font-heading font-black tracking-tight text-3xl">Employees ({employees.length})</h1>
        </div>
        <button
          data-testid="add-employee-btn"
          onClick={() => { setForm(EMPTY); setAddOpen(true); }}
          className="flex items-center gap-2 bg-[#007AFF] text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-[#0066d6] transition-colors duration-150"
        >
          <Plus size={16} weight="bold" /> Add Employee
        </button>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden max-w-full">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left">
              {["ID", "Name", "Mobile", "Department", "Designation", "Status", "Actions"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`employee-row-${emp.employee_id}`}>
                <td className="px-4 py-3 font-mono-data text-xs text-slate-500">{emp.employee_id}</td>
                <td className="px-4 py-3 font-medium">{emp.name}</td>
                <td className="px-4 py-3 font-mono-data text-xs">{emp.mobile_number}</td>
                <td className="px-4 py-3 text-slate-600">{emp.department || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{emp.designation || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm uppercase tracking-wider ${emp.status === "active" ? "bg-blue-50 text-[#007AFF]" : "bg-slate-100 text-slate-500"}`}>
                    {emp.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button data-testid={`edit-employee-${emp.employee_id}`} title="Edit" onClick={() => { setForm(emp); setEditing(emp); }} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors duration-150">
                      <PencilSimple size={14} />
                    </button>
                    <button data-testid={`reset-password-${emp.employee_id}`} title="Reset password" onClick={() => setResetTarget(emp)} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors duration-150">
                      <Key size={14} />
                    </button>
                    <button data-testid={`delete-employee-${emp.employee_id}`} title="Remove" onClick={() => setDeleteTarget(emp)} className="p-1.5 border border-slate-200 rounded-md text-[#FF3B30] hover:bg-red-50 transition-colors duration-150">
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400" data-testid="employees-loading">Loading employees… first load can take up to a minute.</td></tr>
            )}
            {!loading && employees.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No employees yet. Add your first team member.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Add Employee</DialogTitle>
            <DialogDescription>New team member for the mobile app.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addEmployee} className="space-y-4">
            <Field label="Full Name" testId="employee-name-input" value={form.name} onChange={set("name")} required />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Mobile Number" testId="employee-mobile-input" value={form.mobile_number} onChange={set("mobile_number")} required />
              <Field label="Employee ID" testId="employee-id-input" value={form.employee_id} onChange={set("employee_id")} placeholder="EMP-004" required />
            </div>
            <Field label="Password" testId="employee-password-input" type="password" value={form.password} onChange={set("password")} required />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Department" testId="employee-department-input" value={form.department} onChange={set("department")} placeholder="Sales" />
              <Field label="Designation" testId="employee-designation-input" value={form.designation} onChange={set("designation")} placeholder="Field Executive" />
            </div>
            <button data-testid="save-employee-btn" disabled={busy} className="w-full bg-[#007AFF] text-white rounded-md py-2.5 text-sm font-semibold hover:bg-[#0066d6] transition-colors duration-150 disabled:opacity-60">
              {busy ? "Saving…" : "Add Employee"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Edit {editing?.name}</DialogTitle>
            <DialogDescription>Update employee details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={updateEmployee} className="space-y-4">
            <Field label="Full Name" testId="edit-name-input" value={form.name || ""} onChange={set("name")} required />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Department" testId="edit-department-input" value={form.department || ""} onChange={set("department")} />
              <Field label="Designation" testId="edit-designation-input" value={form.designation || ""} onChange={set("designation")} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select data-testid="edit-status-select" value={form.status || "active"} onChange={set("status")} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007AFF]">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <button data-testid="update-employee-btn" disabled={busy} className="w-full bg-[#007AFF] text-white rounded-md py-2.5 text-sm font-semibold hover:bg-[#0066d6] transition-colors duration-150 disabled:opacity-60">
              {busy ? "Saving…" : "Save Changes"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Reset password — {resetTarget?.name}</DialogTitle>
            <DialogDescription>Set a new mobile app password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <Field label="New Password" testId="new-password-input" type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} />
            <p className="text-xs text-slate-500">The employee will use this password on their mobile app.</p>
            <button data-testid="confirm-reset-btn" disabled={busy} className="w-full bg-[#007AFF] text-white rounded-md py-2.5 text-sm font-semibold hover:bg-[#0066d6] transition-colors duration-150 disabled:opacity-60">
              {busy ? "Resetting…" : "Reset Password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-employee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {deleteTarget?.employee_id} from the system. They will no longer be able to log in from the mobile app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={deleteEmployee} className="bg-[#FF3B30] hover:bg-[#d9322a]">
              Remove Employee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
